import React from 'react'
import { Link as RouterLink } from 'react-router-dom'
import { Box, Button, Typography, Paper, useMediaQuery } from '@mui/material'
import EventAvatar from './EventAvatar'
import EventCardActions from './EventCardActions'
import TimeAgoTranslator from '../Utils/TimeAgoTranslator'
import { getHostEndEventPresentation } from '../Utils/eventHostActions'
import { EVENT_CARD_AVATAR_SIZE, EVENT_CARD_MAIN_GAP } from './eventCardLayout'

const timeAgoTranslator = new TimeAgoTranslator()
const DESCRIPTION_TOGGLE_THRESHOLD = 120
const COLLAPSED_DESCRIPTION_LENGTH = 120

const truncateDescription = (description) => {
  const normalized = String(description || '').trim()
  if (normalized.length <= COLLAPSED_DESCRIPTION_LENGTH) return normalized
  return `${normalized.slice(0, COLLAPSED_DESCRIPTION_LENGTH).trimEnd()}...`
}

const EventCard = ({
  playableEvent,
  joinTo,
  joinState,
  showCloseControl = false,
  closeDisabled = false,
  onRequestClose
}) => {
  const compact = useMediaQuery('(max-width:380px)')
  const anearEvent = playableEvent.anearEvent
  const eventName = anearEvent.name
  const eventDescription = anearEvent.attributes.description || ''
  const createdAt = anearEvent.createdAt
  const createdText = createdAt ? timeAgoTranslator.translate(createdAt) : null
  const [isDescriptionExpanded, setIsDescriptionExpanded] = React.useState(false)
  const hasLongDescription = eventDescription.trim().length > DESCRIPTION_TOGGLE_THRESHOLD
  const visibleDescription = hasLongDescription && !isDescriptionExpanded
    ? truncateDescription(eventDescription)
    : eventDescription
  const avatarSize = compact ? EVENT_CARD_AVATAR_SIZE.narrow : EVENT_CARD_AVATAR_SIZE.wide
  const mainGap = compact ? EVENT_CARD_MAIN_GAP.narrow : EVENT_CARD_MAIN_GAP.wide
  const hostEnd = showCloseControl ? getHostEndEventPresentation(anearEvent) : null

  return (
    <Paper
      elevation={0}
      sx={{
        position: 'relative',
        bgcolor: 'rgba(255,255,255,0.08)',
        borderRadius: 2,
        px: { xs: 0.85, sm: 1.2 },
        py: { xs: 1.35, sm: 2.5 },
        minWidth: 0,
        width: '100%',
        boxSizing: 'border-box',
        border: '1px solid rgba(255,255,255,0.1)',
        '&:hover': {
          bgcolor: 'rgba(255,255,255,0.12)',
          borderColor: 'rgba(255,255,255,0.15)',
          boxShadow: '0 8px 24px rgba(0,0,0,0.35)'
        }
      }}
    >
      {createdText && (
        <Typography
          component="p"
          sx={{
            position: 'absolute',
            top: 4,
            right: 8,
            m: 0,
            color: 'rgba(148,163,184,0.95)',
            fontSize: '0.8rem',
            fontStyle: 'italic'
          }}
        >
          {`Created ${createdText}`}
        </Typography>
      )}

      <Box
        sx={{
          display: 'flex',
          alignItems: 'stretch',
          gap: `${mainGap}px`,
          mt: createdText ? 1 : 0,
          minWidth: 0
        }}
      >
        <Box
          component={RouterLink}
          to={joinTo}
          state={joinState}
          sx={{
            flex: 1,
            minWidth: 0,
            display: 'flex',
            alignItems: 'center',
            alignSelf: 'center',
            gap: `${mainGap}px`,
            textDecoration: 'none',
            color: 'inherit'
          }}
        >
          <EventAvatar anearEvent={anearEvent} size={avatarSize} insetOverlay />
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography
              component="h3"
              sx={{
                m: 0,
                color: '#E6E9EF',
                fontWeight: 700,
                fontSize: compact ? '1rem' : '1.1rem',
                lineHeight: 1.25
              }}
            >
              {eventName}
            </Typography>
            {eventDescription && (
              <>
                <Typography
                  component="p"
                  sx={{
                    m: 0,
                    mt: 0.45,
                    color: 'rgba(230,233,239,0.7)',
                    fontSize: compact ? '0.82rem' : '0.875rem',
                    lineHeight: 1.4,
                    wordBreak: 'break-word',
                    ...(hasLongDescription && !isDescriptionExpanded
                      ? {
                          display: '-webkit-box',
                          WebkitLineClamp: 3,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden'
                        }
                      : {})
                  }}
                >
                  {visibleDescription}
                </Typography>
                {hasLongDescription && (
                  <Button
                    size="small"
                    onClick={(event) => {
                      event.preventDefault()
                      event.stopPropagation()
                      setIsDescriptionExpanded((current) => !current)
                    }}
                    sx={{
                      mt: 0.2,
                      px: 0,
                      minWidth: 0,
                      textTransform: 'none',
                      color: '#93C5FD',
                      fontWeight: 700,
                      '&:hover': { bgcolor: 'transparent', textDecoration: 'underline' }
                    }}
                  >
                    {isDescriptionExpanded ? 'less' : 'more'}
                  </Button>
                )}
              </>
            )}
          </Box>
        </Box>

        <EventCardActions
          compact={compact}
          joinTo={joinTo}
          joinState={joinState}
          hostEnd={hostEnd}
          hostEndDisabled={closeDisabled}
          onHostEnd={onRequestClose}
        />
      </Box>
    </Paper>
  )
}

export default EventCard
